const CHAT_BASE = '/goapi/chat';

export class ChatApiError extends Error {
    constructor(message, {status = 0, code = 'REQUEST_FAILED', requestId = null} = {}) {
        super(message);
        this.name = 'ChatApiError';
        this.status = status;
        this.code = code;
        this.requestId = requestId;
    }
}
function chatHeaders(hasBody = false) {
    const token = localStorage.getItem('cosToken');
    const headers = new Headers();
    if (hasBody) headers.set('Content-Type', 'application/json');
    if (token) {
        headers.set('Authorization', /^Bearer\s/i.test(token) ? token : `Bearer ${token}`);
    }
    return headers;
}

async function chatRequest(path, {method = 'GET', body, signal} = {}) {
    const response = await fetch(`${CHAT_BASE}${path}`, {
        method,
        headers: chatHeaders(body !== undefined),
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
        redirect: 'follow',
    });
    if (response.status === 401) {
        localStorage.removeItem('cosToken');
        window.location.assign('/login');
    }
    if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const details = payload?.error || {};
        throw new ChatApiError(details.message || `Chat request failed (${response.status})`, {
            status: response.status,
            code: details.code,
            requestId: details.request_id || response.headers.get('X-Request-ID'),
        });
    }
    if (response.status === 204) return null;
    return response.json();
}

async function signedBlobRequest(url, signal) {
    const response = await fetch(url, {signal});
    if (!response.ok) {
        throw new ChatApiError(`Attachment download failed (${response.status})`, {
            status: response.status,
            code: 'ATTACHMENT_DOWNLOAD_FAILED',
        });
    }
    return response.blob();
}

export function listChatUsers(signal, scope) {
    const query = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    return chatRequest(`/users${query}`, {signal});
}

export async function listAllConversations(signal) {
    const conversations = [];
    let afterId = 0;
    do {
        const params = new URLSearchParams({member_only: 'true', summary: 'true', limit: '100'});
        if (afterId > 0) params.set('after_id', String(afterId));
        const page = await chatRequest(`/conversations?${params}`, {signal});
        const batch = page?.conversations || [];
        conversations.push(...batch);
        if (batch.length < 100) break;
        afterId = batch[batch.length - 1].id;
    } while (true);
    return conversations;
}

export function getChatBootstrap({selectedId, signal} = {}) {
    const params = new URLSearchParams({limit: '50'});
    if (selectedId) params.set('selected_id', String(selectedId));
    return chatRequest(`/bootstrap?${params}`, {signal});
}

export function listConversationFeed({cursor, agentKey, query, signal} = {}) {
    const params = new URLSearchParams({sort: 'activity', summary: 'true', limit: '50'});
    if (cursor) params.set('cursor', cursor);
    if (agentKey) params.set('agent_key', agentKey);
    if (query) params.set('q', query);
    return chatRequest(`/conversations?${params}`, {signal});
}

export function listConversationGroups(signal) {
    return chatRequest('/conversation-groups?type=agent', {signal});
}

export function getUnreadStatus(signal) {
    return chatRequest('/unread-status', {signal});
}

export function queryConversationSummaries(conversationIds, signal) {
    return chatRequest('/conversation-summaries/query', {
        method: 'POST', body: {conversation_ids: conversationIds}, signal,
    });
}

export function createConversation(payload) {
    return chatRequest('/conversations', {method: 'POST', body: payload});
}

export function ensurePropertyChannel(propertyId, cosJobId, signal) {
    return chatRequest(`/property-channels/${encodeURIComponent(propertyId)}`, {
        method: 'PUT',
        body: {cos_job_id: cosJobId},
        signal,
    });
}

export function getConversation(conversationId, signal) {
    return chatRequest(`/conversations/${encodeURIComponent(conversationId)}`, {signal});
}

export function listMessages(conversationId, {beforeSequence, afterSequence, signal} = {}) {
    const params = new URLSearchParams({limit: '50'});
    if (beforeSequence) params.set('before_sequence', String(beforeSequence));
    if (afterSequence) params.set('after_sequence', String(afterSequence));
    return chatRequest(`/conversations/${conversationId}/messages?${params}`, {signal});
}

export function listReplies(conversationId, rootId, {beforeSequence, signal} = {}) {
    const params = new URLSearchParams({limit: '50'});
    if (beforeSequence) params.set('before_sequence', String(beforeSequence));
    return chatRequest(`/conversations/${conversationId}/messages/${rootId}/replies?${params}`, {signal});
}

export function createMessage(conversationId, body, clientMessageId, attachmentIds = []) {
    return chatRequest(`/conversations/${conversationId}/messages`, {
        method: 'POST', body: {client_message_id: clientMessageId, body: body || null, attachment_ids: attachmentIds},
    });
}

export function createReply(conversationId, rootId, body, clientMessageId, attachmentIds = []) {
    return chatRequest(`/conversations/${conversationId}/messages/${rootId}/replies`, {
        method: 'POST', body: {client_message_id: clientMessageId, body: body || null, attachment_ids: attachmentIds},
    });
}

export function createAttachmentUpload(payload, signal) {
    return chatRequest('/attachments/uploads', {method: 'POST', body: payload, signal});
}

export function completeAttachmentUpload(attachmentId, signal) {
    return chatRequest(`/attachments/${attachmentId}/complete`, {method: 'POST', body: {}, signal});
}

export async function sha256Hex(file) {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, '0')).join('');
}

export function uploadAttachmentObject(uploadUrl, headers, file, {signal, onProgress} = {}) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        const abort = () => request.abort();
        const cleanUp = () => signal?.removeEventListener('abort', abort);
        request.open('PUT', uploadUrl);
        Object.entries(headers || {}).forEach(([name, value]) => {
            // Browsers set Content-Length themselves and disallow scripts from setting it.
            if (name.toLowerCase() !== 'content-length') request.setRequestHeader(name, value);
        });
        request.upload.onprogress = event => {
            if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
        };
        request.onload = () => {
            cleanUp();
            if (request.status >= 200 && request.status < 300) {
                onProgress?.(100);
                resolve();
                return;
            }
            reject(new ChatApiError(`Attachment upload failed (${request.status})`, {
                status: request.status,
                code: 'ATTACHMENT_UPLOAD_FAILED',
            }));
        };
        request.onerror = () => {
            cleanUp();
            reject(new ChatApiError('Attachment upload failed', {code: 'ATTACHMENT_UPLOAD_FAILED'}));
        };
        request.onabort = () => {
            cleanUp();
            reject(new DOMException('Attachment upload cancelled', 'AbortError'));
        };
        if (signal?.aborted) {
            reject(new DOMException('Attachment upload cancelled', 'AbortError'));
            return;
        }
        signal?.addEventListener('abort', abort, {once: true});
        request.send(file);
    });
}

export async function uploadChatAttachment(conversationId, file, {contentType = file.type, signal, onProgress} = {}) {
    onProgress?.({stage: 'hashing', progress: 0});
    const checksum = await sha256Hex(file);
    const allocation = await createAttachmentUpload({
        conversation_id: conversationId,
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size,
        sha256: checksum,
    }, signal);
    onProgress?.({stage: 'uploading', progress: 0});
    await uploadAttachmentObject(allocation.upload_url, allocation.headers, file, {
        signal,
        onProgress: progress => onProgress?.({stage: 'uploading', progress}),
    });
    onProgress?.({stage: 'verifying', progress: 100});
    const completed = await completeAttachmentUpload(allocation.attachment_id, signal);
    return {
        id: allocation.attachment_id,
        original_filename: file.name,
        content_type: completed.content_type || contentType,
        size_bytes: file.size,
        download_path: `/goapi/chat/attachments/${allocation.attachment_id}/download`,
        local_file: file,
    };
}

export function createAttachmentDownload(attachment, signal) {
    const path = attachment.download_path || `/goapi/chat/attachments/${attachment.id}/download`;
    const relativePath = path.startsWith(CHAT_BASE) ? path.slice(CHAT_BASE.length) : path;
    const separator = relativePath.includes('?') ? '&' : '?';
    return chatRequest(`${relativePath}${separator}format=json`, {signal});
}

export async function loadChatAttachmentBlob(attachment, signal) {
    const allocation = await createAttachmentDownload(attachment, signal);
    return signedBlobRequest(allocation.download_url, signal);
}

export async function downloadChatAttachment(attachment) {
    const blob = await loadChatAttachmentBlob(attachment);
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = attachment.original_filename || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

export function updateMessage(messageId, body, version) {
    return chatRequest(`/messages/${messageId}`, {method: 'PATCH', body: {body, version}});
}

export function deleteMessage(messageId) {
    return chatRequest(`/messages/${messageId}`, {method: 'DELETE'});
}

export function addReaction(messageId, emoji) {
    return chatRequest(`/messages/${messageId}/reactions`, {method: 'PUT', body: {emoji}});
}

export function removeReaction(messageId, emoji) {
    return chatRequest(`/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`, {method: 'DELETE'});
}

export function updateReadCursor(conversationId, throughSequence) {
    return chatRequest(`/conversations/${conversationId}/read`, {
        method: 'PUT', body: {through_sequence: throughSequence},
    });
}

export function updateThreadState(rootId, payload) {
    return chatRequest(`/messages/${rootId}/thread-state`, {method: 'PUT', body: payload});
}

export function createWebSocketTicket(deviceId) {
    return chatRequest('/ws-ticket', {method: 'POST', body: {device_id: deviceId}});
}

export function syncEvents(afterEventId, signal) {
    return chatRequest(`/sync?after_event_id=${afterEventId}`, {signal});
}

export function getPushConfig(signal) {
    return chatRequest('/push-config', {signal});
}

export function registerPushToken(deviceId, payload, signal) {
    return chatRequest(`/devices/${encodeURIComponent(deviceId)}/push-token`, {
        method: 'PUT', body: payload, signal,
    });
}

export function deletePushToken(deviceId, signal) {
    return chatRequest(`/devices/${encodeURIComponent(deviceId)}/push-token`, {
        method: 'DELETE', signal,
    });
}

export function chatWebSocketUrl() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${CHAT_BASE}/ws`;
}
